import { useEffect, useRef, useState } from 'react';
import { Button } from '../common';
import SvgFigureEditor from './svg/SvgFigureEditor';
import SvgFigurePreview from './svg/SvgFigurePreview';
import { fromDataUri, readStamp } from './svg/figureSvg';
import { checkSanitizerVersion, checkSvg } from './svg/svgPolicy';
import { downloadPng, downloadSvg } from './svg/figureDownload';
import {
  AUTH_REDIRECT, fetchFigures, fetchFigureHistory, fetchFigureHistoryVersion,
  readVersionContent, restoreFigure, restoreOriginal, saveFigureSvg, replaceFigureImage,
} from './figureApi';

const svgOf = (item) => item?.svg_text || fromDataUri(item?.src) || '';
const revisionOf = (row) => row.revision ?? row.version;
const sourceName = (row) => row.source_name || ({ 1000: 'SVG 이관', 1100: '변형' }[row.source]) || '그림 변경';

/** FigureModal의 대조·권한 검사를 통과한 항목만 쓴다. 편집 초안과 서버 정제본은 분리한다. */
export default function SvgFigureWorkspace({
  target, problemId, field, item, token, writable, busy, setBusy, getAccessToken,
  onSettled, onRequestAi, cropSrc, initialHistory = false, externalError,
}) {
  const editor = useRef(null);
  const invalid = useRef(false);
  const [loaded, setLoaded] = useState(svgOf(item));
  const [draft, setDraft] = useState(svgOf(item));
  const [dirty, setDirty] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [backup, setBackup] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(initialHistory);
  const [history, setHistory] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const cache = useRef(new Map());
  const sequence = useRef(0);
  const callbacks = useRef({ getAccessToken, onSettled });
  callbacks.current = { getAccessToken, onSettled };
  const stamp = readStamp(item?.src);
  const editable = item?.kind === 'svg' && !!stamp && stamp.figureUuid === item.figure_uuid
    && stamp.revision === item.revision;
  const identity = `${target}/${problemId}/${field}/${item.index}/${item.figure_uuid || 'pixel'}`;

  const install = (text, changed = false) => {
    invalid.current = false;
    setLoaded(text); setDraft(text); setDirty(changed); setEditorKey((n) => n + 1);
    setSelected(null); setPreview(null);
  };
  useEffect(() => {
    install(svgOf(item));
    setHistory(null); setHistoryBusy(false); setHistoryError(null); setError(null); setConflict(false);
    checkSanitizerVersion(item.sanitizer_version);
    sequence.current += 1;
    // 판 번호가 다시 1이 되어도 다른 그림의 캐시는 가져오지 않는다.
    cache.current.clear();
    return () => { sequence.current += 1; };
    // 서버의 새 판을 받았을 때만 편집기를 초기화한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, item.revision, item.src]);

  const fail = (e) => {
    const reason = e?.data?.reason;
    if (reason === 'stale_revision' || reason === 'stale') {
      setConflict(true);
      setError('다른 곳에서 그림이 바뀌었습니다. 초안은 그대로 남아 있습니다. 다시 읽은 뒤 비교해 주세요.');
    } else {
      setError(e?.message || '요청을 처리하지 못했습니다.');
    }
  };
  useEffect(() => { if (externalError) fail(externalError); }, [externalError]);
  const write = async (name, request, message) => {
    if (!writable || busy || conflict) return;
    setBusy(name); setError(null);
    try {
      const result = await request();
      if (result === AUTH_REDIRECT) return;
      callbacks.current.onSettled(result, message);
      setBackup(null);
    } catch (e) { fail(e); } finally { setBusy(null); }
  };
  const save = () => {
    if (selected) {
      if (selected.kind === 'original') return write('drop', () => restoreOriginal(target, problemId, field, item.index,
        { expectedUpdatedAt: token }, callbacks.current.getAccessToken), '원본 사진으로 되돌렸습니다.');
      if (selected.kind === 'crop') return write('save', () => replaceFigureImage(target, problemId, field, item.index,
        { image: cropSrc, expectedUpdatedAt: token }, callbacks.current.getAccessToken), '교재 크롭 사진으로 바꿨습니다.');
      return write('restore', () => restoreFigure(target, problemId, field, item.index, selected.revision,
        { expectedUpdatedAt: token }, callbacks.current.getAccessToken), '고른 판으로 되돌렸습니다. 새 판으로 저장됩니다.');
    }
    invalid.current = false;
    const text = editor.current?.getSvgText();
    // 편집기는 무효 입력에서 마지막 유효본을 반환한다. 그 값을 몰래 저장하지 않는다.
    if (invalid.current || !text || !checkSvg(text).ok) {
      setError('그림에 저장할 수 없는 내용이 있습니다. 초안을 확인해 주세요.'); return;
    }
    write('save', () => saveFigureSvg(target, problemId, field, item.index,
      { svgText: text, expectedRevision: item.revision, expectedUpdatedAt: token }, callbacks.current.getAccessToken), '그림을 저장했습니다.');
  };
  const reload = async () => {
    if (busy) return;
    setBusy('reload');
    try {
      const result = await fetchFigures(target, problemId, callbacks.current.getAccessToken);
      if (result === AUTH_REDIRECT) return;
      // 순번이 밀린 경우 UUID로 다시 찾는다. 픽셀은 같은 바이트가 유일할 때만 허용한다.
      const matches = (result.items || []).filter((it) => it.field === field && (item.figure_uuid
        ? it.figure_uuid === item.figure_uuid : it.src === item.src));
      if (matches.length !== 1) throw new Error('이 그림이 이동하거나 교체되었습니다. 초안을 내려받고 창을 다시 열어 주세요.');
      if (dirty) setBackup({ text: draft, uuid: item.figure_uuid });
      callbacks.current.onSettled({ ...result, item: matches[0] }, '최신 그림을 읽었습니다. 이전 초안은 이 창에 보관했습니다.');
      install(svgOf(matches[0])); setConflict(false); setError(null);
    } catch (e) { fail(e); } finally { setBusy(null); }
  };
  const loadHistory = async () => {
    const seq = sequence.current;
    setHistoryBusy(true); setHistoryError(null);
    try {
      const result = await fetchFigureHistory(target, problemId, field, item.index, callbacks.current.getAccessToken);
      if (result === AUTH_REDIRECT || seq !== sequence.current) return;
      if (item.figure_uuid && result.figure_uuid !== item.figure_uuid) throw new Error('그림이 바뀌었습니다. 다시 읽어 주세요.');
      setHistory(result);
    } catch (e) { if (seq === sequence.current) setHistoryError(e.message); }
    finally { if (seq === sequence.current) setHistoryBusy(false); }
  };
  useEffect(() => {
    if (historyOpen && !history && !historyBusy && !historyError) loadHistory();
    // 요청 함수의 정체성으로 재조회하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen, history, historyBusy, historyError, identity, item.revision]);
  const viewVersion = async (row) => {
    const revision = revisionOf(row), seq = sequence.current;
    const key = `${identity}/${revision}`;
    setHistoryBusy(true); setHistoryError(null);
    try {
      let content = cache.current.get(key);
      if (!content) {
        const result = await fetchFigureHistoryVersion(target, problemId, field, item.index, revision, callbacks.current.getAccessToken);
        if (result === AUTH_REDIRECT || seq !== sequence.current) return;
        content = readVersionContent(result);
        if (!content) throw new Error('이 판의 그림을 받지 못했습니다. 다시 시도해 주세요.');
        cache.current.set(key, content);
      }
      setPreview({ ...content, revision });
    } catch (e) { if (seq === sequence.current) setHistoryError(e.message); }
    finally { if (seq === sequence.current) setHistoryBusy(false); }
  };
  const choose = (selection) => { setSelected(selection); setPreview(null); };
  const visible = preview || (selected?.kind === 'original' ? { src: item.original_src }
    : selected?.kind === 'crop' ? { src: cropSrc } : selected?.content);
  const disabled = !writable || !!busy || conflict;
  const busyReconvert = busy === 'reconvert';
  const downloadText = preview ? svgOf(preview) : draft;
  const download = async (png) => {
    try {
      const text = downloadText;
      if (png) await downloadPng(text, 'figure.png', { scale: 2, transparent: true });
      else downloadSvg(text, 'figure.svg');
    } catch (e) { setError(e.message || '그림을 내려받지 못했습니다.'); }
  };

  return <div className="space-y-3">
    {busy && !busyReconvert && <p role="status" className="text-xs text-gray-600">
      {busy === 'reload' ? '최신 그림을 읽고 있습니다.' : '그림을 저장하고 있습니다.'}</p>}
    {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-xs text-danger">{error}</p>}
    {conflict && <Button size="xs" onClick={reload} disabled={!!busy}>다시 읽기</Button>}
    {backup && <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
      이전 초안을 보관하고 있습니다.
      <Button size="xs" variant="secondary" disabled={disabled || !editable || backup.uuid !== item.figure_uuid}
        onClick={() => install(backup.text, true)}>이전 초안 가져오기</Button>
      <Button size="xs" variant="secondary" onClick={() => downloadSvg(backup.text, 'figure-draft.svg')}>이전 초안 다운로드</Button>
    </div>}
    <div className="grid gap-3 xl:grid-cols-[minmax(200px,0.6fr)_minmax(0,1.4fr)]">
      <div className="flex min-w-0 flex-col gap-3">
        <section className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3 xl:flex-auto">
          <h3 className="text-xs font-semibold text-gray-700">원본 사진</h3>
          <div className="flex flex-auto items-center justify-center">
            {item.has_original && item.original_src ? <SvgFigurePreview src={item.original_src} alt="원본 사진" className="max-h-44 object-contain" />
              : <p className="text-xs text-gray-500">{item.has_original ? '보관된 원본의 미리보기가 없습니다.' : '보관된 원본 사진이 없습니다.'}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="xs" variant="secondary" disabled={disabled || !item.has_original}
              onClick={() => choose({ kind: 'original' })}>원본으로 되돌리기</Button>
            <Button size="xs" variant="secondary" loading={busyReconvert} aria-busy={busyReconvert}
              disabled={disabled || dirty || !!selected || !!preview}
              onClick={onRequestAi}>{busyReconvert ? '그리는 중…' : 'AI로 다시 그리기'}</Button>
          </div>
          {cropSrc && cropSrc !== item.original_src && <Button size="xs" variant="secondary" className="self-start" disabled={disabled}
            onClick={() => choose({ kind: 'crop' })}>교재 크롭으로 바꾸기</Button>}
        </section>
        <section className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3 xl:flex-auto">
          <h3 className="text-xs font-semibold text-gray-700">{preview ? `이력 ${preview.revision}판 미리보기` : '미리보기'}</h3>
          <div className="flex flex-auto items-center justify-center">
            {selected?.kind === 'original' && !item.original_src
              ? <p className="text-xs text-gray-500">서버에 보관된 원본 사진으로 바뀝니다. 미리보기는 제공되지 않았습니다.</p>
              : <SvgFigurePreview src={visible?.src || item.src} svgText={!visible && editable ? draft : undefined}
                width={item.width} height={item.height} alt="그림 미리보기" className="object-contain" />}
          </div>

          {preview && <div className="flex flex-wrap gap-2">
            <Button size="xs" variant="secondary" onClick={() => setPreview(null)}>초안으로 돌아가기</Button>
            <Button size="xs" disabled={disabled} onClick={() => choose({ kind: 'history', revision: preview.revision, content: preview })}>이 판으로 되돌리기</Button>
            {editable && svgOf(preview) && <Button size="xs" disabled={disabled}
              onClick={() => install(svgOf(preview), true)}>편집기로 가져오기</Button>}
          </div>}
        </section>
      </div>
      <section className="svg-figure-editor-pane flex min-w-0 flex-col gap-2">
        <h3 className="text-xs font-semibold text-gray-700">그림 편집{item.revision ? ` · ${item.revision}판` : ''}</h3>
        {editable ? <SvgFigureEditor key={editorKey} ref={editor} svgText={loaded}
          readOnly={disabled || !!selected || !!preview}
          onChange={(text) => {
            setDraft(text);
            const saved = checkSvg(svgOf(item));
            setDirty(text !== (saved.ok ? saved.svg : svgOf(item)));
            invalid.current = false;
          }}
          onInvalid={({ reason }) => { invalid.current = true; setError(reason); }} />
          : <p className="rounded-xl bg-gray-50 p-4 text-xs text-gray-600">이 그림은 직접 편집할 수 없습니다. AI로 다시 그린 뒤 편집해 주세요.</p>}
        {dirty && <p className="text-xs text-gray-500">저장하지 않은 초안이 있습니다.</p>}
        {item.warnings?.length > 0 && <p className="text-xs text-amber-800">그림 정제 안내: {item.warnings.join(' · ')}</p>}
      </section>
    </div>
    {selected && <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">바꿀 그림을 골랐습니다. 저장을 눌러야 반영됩니다.
      <Button size="xs" variant="secondary" className="ml-2" disabled={!!busy} onClick={() => setSelected(null)}>고르기 취소</Button>
    </p>}
    <div className="flex flex-wrap items-center gap-2">
      <Button size="xs" variant="secondary" onClick={() => setHistoryOpen((v) => !v)} disabled={!!busy}>{historyOpen ? '이력 접기' : '이력'}</Button>
      <Button size="xs" variant="secondary" onClick={() => download(false)} disabled={!downloadText || !!selected}>SVG 다운로드</Button>
      <Button size="xs" variant="secondary" onClick={() => download(true)} disabled={!downloadText || !!selected}>PNG 다운로드</Button>
      <Button size="sm" className="ml-auto" disabled={disabled || !!preview || (!selected && (!editable || !dirty))} onClick={save}>저장</Button>
    </div>
    {historyOpen && <section className="space-y-2 rounded-xl border border-gray-200 p-3">
      <div className="flex items-center gap-2"><h3 className="text-xs font-semibold">이력</h3>
        <Button size="xs" variant="secondary" onClick={loadHistory} disabled={historyBusy || !!busy}>새로 읽기</Button></div>
      {historyBusy && <p role="status" className="text-xs text-gray-500">이력을 읽는 중입니다.</p>}
      {historyError && <p role="alert" className="text-xs text-danger">{historyError}</p>}
      {history?.versions?.length === 0 && <p className="text-xs text-gray-500">이력이 없습니다.</p>}
      {history?.versions?.map((row) => <div key={`${identity}/${revisionOf(row)}`} className="flex flex-wrap items-center gap-2 border-t border-gray-100 py-2 text-xs text-gray-600">
        {cache.current.has(`${identity}/${revisionOf(row)}`) && <SvgFigurePreview
          src={cache.current.get(`${identity}/${revisionOf(row)}`).src} alt={`${revisionOf(row)}판 그림`} className="size-12 object-contain" />}
        <span>{revisionOf(row)}판 · {sourceName(row)}{revisionOf(row) === (history.head_revision ?? history.current_version) ? ' · 현재' : ''}</span>
        <Button size="xs" variant="secondary" disabled={historyBusy || !!busy} onClick={() => viewVersion(row)}>보기</Button>
      </div>)}
    </section>}
    <p role="status" aria-live="polite" className="sr-only">
      {busyReconvert ? 'AI가 그림을 다시 그리고 있습니다.' : ''}
    </p>
  </div>;
}
